import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Chip,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Alert
} from '@mui/material';
import moment from 'moment';
import axios from 'axios';
import { APIURL } from '../../config';

const VALID_STATUSES = [
  { value: 'REFERRAL_INITIATED', label: 'Referral Initiated' },
  { value: 'INITIAL_CONTACT', label: 'Initial Contact' },
  { value: 'DOCUMENTATION_PENDING', label: 'Documentation Pending' },
  { value: 'DOCUMENTATION_RECEIVED', label: 'Documentation Received' },
  { value: 'DETERMINATION_PENDING', label: 'Determination Pending' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'DENIED', label: 'Denied' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'INTAKE_IN_PROGRESS', label: 'Intake in Progress' },
  { value: 'CLOSED', label: 'Closed' },
];

// Set only from the Incoming Referrals review; the status editor is read-only for them.
const DECISION_STATUSES = [
  { value: 'RECEIVED', label: 'Received' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'DUPLICATE', label: 'Duplicate' },
];

const formatStatus = (status) => {
  const found = [...VALID_STATUSES, ...DECISION_STATUSES].find(s => s.value === status);
  return found ? found.label : status;
};

const Labeled = ({ label, children }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}:</Typography>
    <Typography variant="body2">{children}</Typography>
  </Stack>
);

export default function ReferralDetailsModal({ open, onClose, referral, comp_id, apiRef, user }) {
  const [statusValue, setStatusValue] = useState('REFERRAL_INITIATED');
  const [originalStatus, setOriginalStatus] = useState('REFERRAL_INITIATED');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [note, setNote] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [customers, setCustomers] = useState([]);
  const [linkedCustomerId, setLinkedCustomerId] = useState(null);
  const [customerToLink, setCustomerToLink] = useState(null);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const getNotesFromReferral = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return parsed?.notes || '';
      } catch {
        return '';
      }
    }
    return value.notes || '';
  };

  const isAdmin = user?.type === 'Admin';
  const decisionStatus = DECISION_STATUSES.find(s => s.value === String(referral?.referral_status || '').toUpperCase());
  const canChangeStatus = !decisionStatus && (isAdmin || referral?.assigned_emp_id === user?.emp_id);

  useEffect(() => {
    if (!referral) {
      setStatusValue('REFERRAL_INITIATED');
      setOriginalStatus('REFERRAL_INITIATED');
      return;
    }
    const normalized = String(referral.referral_status || referral.status || 'REFERRAL_INITIATED').toUpperCase().replace(/ /g, '_');
    const nextNote = getNotesFromReferral(referral.data);
    setStatusValue(normalized);
    setOriginalStatus(normalized);
    setStatusError(null);
    setNote(nextNote);
    setSavedNote(nextNote);
  }, [referral, open]);

  useEffect(() => {
    if (!open || !referral || !(referral.company_role === 'receiving' || Number(referral.receiver_com_id) === Number(comp_id))) return;
    let data = {};
    try { data = typeof referral.data === 'string' ? JSON.parse(referral.data) : (referral.data || {}); } catch (_) { /* no link */ }
    if (data.receiving_customer_id) return;
    let active = true;
    axios.get(`${APIURL}/customers/getCustomersList`, { params: { company_id: comp_id } })
      .then(response => { if (active) setCustomers(response.data?.customers || []); })
      .catch(() => { if (active) setLinkError('Unable to load customers for this company.'); });
    return () => { active = false; };
  }, [open, referral, comp_id]);

  useEffect(() => {
    const value = referral?.data;
    let parsed = {};
    try { parsed = typeof value === 'string' ? JSON.parse(value) : (value || {}); } catch (_) { /* no link */ }
    setLinkedCustomerId(parsed.receiving_customer_id || null);
    setCustomerToLink(null);
    setLinkError(null);
  }, [referral, open]);

  if (!referral) {
    return null;
  }

  const status = statusValue;
  const date = referral.referral_date || referral.created_at;
  const role = referral.company_role || (Number(referral.initiator_com_id) === Number(comp_id) ? 'initiated' : (Number(referral.receiver_com_id) === Number(comp_id) ? 'receiving' : ''));
  const parseDataObject = (value) => {
    if (!value) return {};
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return value && typeof value === 'object' ? value : {};
  };

  const dataPayload = parseDataObject(referral.data);
  const customerName = referral.customer_name
    || referral.client_name
    || referral.referring_customer_name
    || dataPayload.referring_customer_name
    || dataPayload.customer_name
    || dataPayload.client_name
    || referral.customer
    || '—';

  const handleLinkCustomer = async () => {
    if (!customerToLink) return;
    setLinkSaving(true);
    setLinkError(null);
    try {
      const response = await axios.put(`${APIURL}/referral/${referral.referral_id}/customer`, {
        customer_id: customerToLink.cus_id,
        company_id: comp_id,
      });
      if (response.data?.code !== 200 || Number(response.data?.customer_id) !== Number(customerToLink.cus_id)) {
        throw new Error('The API did not confirm the customer link. Check that the backend is up to date.');
      }
      setLinkedCustomerId(response.data.customer_id);
    } catch (err) {
      const message = err.response?.data?.message;
      const status = err.response?.status;
      setLinkError(message || (status === 404
        ? 'Customer link endpoint not found on this API. Deploy or restart the backend, then retry.'
        : status ? `Could not link the referral (HTTP ${status}). Please check the API logs.`
          : err.message || 'Could not reach the API. Please check your connection.'));
    } finally {
      setLinkSaving(false);
    }
  };

  const persistStatus = async (nextStatus, previousStatus) => {
    if (!referral?.referral_id) {
      return;
    }
    console.log('[ReferralDetailsModal] user object:', user);
    console.log('[ReferralDetailsModal] user.emp_id:', user?.emp_id);
    setStatusSaving(true);
    setStatusError(null);
    try {
      await axios.put(`${APIURL}/referral/${referral.referral_id}/status`, {
        referral_status: nextStatus,
        emp_id: user?.emp_id,
        note: note.trim() || null,
      });
      if (apiRef?.current) {
        const rowId = referral.referral_id ?? referral.id;
        if (rowId) {
          apiRef.current.updateRows([
            {
              id: rowId,
              status: nextStatus,
              raw: { ...referral, referral_status: nextStatus },
            },
          ]);
        }
      }
      setSavedNote(note.trim());
    } catch (error) {
      console.log('Failed to update referral status', error);
      setStatusError('Failed to update status. Please try again.');
      setStatusValue(previousStatus);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleStatusChange = (event) => {
    const nextStatus = String(event.target.value).toUpperCase();
    setStatusValue(nextStatus);
  };

  const handleSaveStatus = async () => {
    await persistStatus(statusValue, originalStatus);
    setOriginalStatus(statusValue);
    apiRef?.current?.updateRows([
      {
        id: referral.referral_id ?? referral.id,
        status: statusValue,
        raw: { ...referral, referral_status: statusValue, data: JSON.stringify({ ...(referral.data && typeof referral.data === 'object' ? referral.data : {}), notes: note.trim() || undefined }) }
      },
    ]);
  };

  const hasStatusChanged = statusValue !== originalStatus;
  const hasNoteChanged = note.trim() !== savedNote;
  const canSave = (hasStatusChanged || hasNoteChanged) && canChangeStatus && !statusSaving;

  const statusColor = (s) => {
    const us = String(s || '').toUpperCase();
    if (['REFERRAL_INITIATED', 'INITIAL_CONTACT', 'DOCUMENTATION_PENDING'].includes(us)) return 'info';
    if (['DOCUMENTATION_RECEIVED', 'DETERMINATION_PENDING', 'INTAKE_IN_PROGRESS'].includes(us)) return 'warning';
    if (us === 'ACCEPTED' || us === 'CLOSED') return 'success';
    if (us === 'DENIED') return 'error';
    if (us === 'ON_HOLD') return 'default';
    return 'default';
  };

  const roleChip = role === 'initiated'
    ? <Chip size="small" color="success" label="Outgoing" />
    : role === 'receiving'
      ? <Chip size="small" color="error" label="Incoming" />
      : null;

  const initiator = referral.initiator_external_name || (referral.initiator_com_id ? `Company #${referral.initiator_com_id}` : '—');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{referral.referral_subject || 'Referral Details'}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {roleChip}
            <Chip size="small" label={formatStatus(status) || '—'} color={statusColor(status)} variant="outlined" />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Labeled label="Subject">{referral.referral_subject || '—'}</Labeled>
          <Labeled label="Date">{date ? moment(date).format('MMM D, YYYY') : '—'}</Labeled>
          <Divider />
          <Labeled label="Initiator">{initiator}</Labeled>
          <Labeled label="Customer">{customerName}</Labeled>
          {role === 'receiving' && !linkedCustomerId && !['RECEIVED', 'REJECTED'].includes(decisionStatus?.value) && (
            <>
              <Divider />
              <Autocomplete
                options={customers}
                value={customerToLink}
                onChange={(_, value) => setCustomerToLink(value)}
                getOptionLabel={option => `${option.cus_name} (#${option.cus_id})`}
                isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
                renderInput={params => <TextField {...params} label="Link to customer" size="small" />}
              />
              <Button variant="outlined" disabled={!customerToLink || linkSaving} onClick={handleLinkCustomer}>
                {linkSaving ? 'Linking...' : 'Link'}
              </Button>
              {linkError && <Alert severity="error">{linkError}</Alert>}
            </>
          )}
          
          {statusError && <Alert severity="error" onClose={() => setStatusError(null)}>{statusError}</Alert>}
          
          {!canChangeStatus && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {decisionStatus?.value === 'RECEIVED'
                ? 'Accept, reject or mark this referral as a duplicate from Incoming Referrals.'
                : decisionStatus ? 'This referral has been closed by its review decision.'
                  : 'You can only change status for referrals assigned to you.'}
            </Alert>
          )}
          
          <FormControl fullWidth size="small" disabled={statusSaving || !canChangeStatus}>
            <InputLabel id="referral-status-label">Status</InputLabel>
            <Select
              labelId="referral-status-label"
              value={status}
              label="Status"
              onChange={handleStatusChange}
            >
              {VALID_STATUSES.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
              {decisionStatus && <MenuItem value={decisionStatus.value} disabled>{decisionStatus.label}</MenuItem>}
            </Select>
          </FormControl>
          
          <TextField
            label="Note"
            multiline
            rows={2}
            fullWidth
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={statusSaving || !canChangeStatus}
          />
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveStatus}
            disabled={!canSave}
          >
            {statusSaving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
